import { z } from 'zod';
import { queueCountsSchema } from './jobs.js';
import { cursorPageQuerySchema } from './pagination.js';
import { PLAN_STATUSES } from './users.js';
/**
 * Plan operations.
 *
 * A `PlanWeek` records only where generation ended up — `FAILED`, with one
 * sentence of reason. `PlanGenerationRun` records how it got there: what asked
 * for it, how long it took, whether the repair round ran, and the validator's
 * own message. One row per attempt, so a week retried three times is three
 * rows rather than one row overwritten twice.
 *
 * **There is no model, token or cost field here.** The generation path cannot
 * supply them — they are known only inside the LLM client — and a field that is
 * always null is a worse answer than an absent one. `LlmCall` (Phase 10)
 * records them against the run id, and spend is read from there.
 */
/** Mirrors Prisma's PlanRunTrigger. */
export const PLAN_RUN_TRIGGERS = ['user_request', 'weekly_cron', 'admin_retry', 'context_change'];
/**
 * Mirrors Prisma's PlanRunStatus.
 *
 * The outcomes are named apart rather than collapsed, because they are
 * different jobs for whoever is reading: `provider_error` is BullMQ's problem
 * and retries itself, `model_error` is the model putting something in the week
 * it was told it could not, and `succeeded_with_residual` is nobody's
 * emergency — a user has their plan, and the numbers behind it are worth
 * watching in aggregate. `failed` is the last resort — something else threw —
 * so an unexpected failure is not left on the screen looking like a run still
 * in flight.
 */
export const PLAN_RUN_STATUSES = [
    'running',
    'succeeded',
    /**
     * A plan was committed and it is not exact. `validationFailure` carries the
     * numbers it missed. Filed apart from `succeeded` because the two questions
     * an operator asks here are different ones: "did this user get a week" and
     * "how often are we approximating".
     */
    'succeeded_with_residual',
    /** No longer written. Historic rows only: see the Prisma enum's comment. */
    'validation_failed',
    'model_error',
    'provider_error',
    'failed',
    /** Generated fine, but a newer request had taken the week by activation. */
    'obsolete',
];
/**
 * Which plans a Plan Ops read covers.
 *
 * Every read on this screen used to exclude a Premium meal plan's runs, which
 * made them invisible rather than separable. `workouts` is that old behaviour
 * kept as a choice — and it is the one that must own a run whose week has
 * since been deleted, because a meal plan keeps its rows and a weekly one
 * does not.
 */
export const PLAN_RUN_KINDS = ['all', 'workouts', 'meals'];
/**
 * Which plan one run was for. The facet's two real members — `all` is a
 * filter, never a row — so that a list showing both can say which is which,
 * and a `meals` row can be shown without a retry control: a meal plan is its
 * owner's to make again, and the API refuses one here.
 */
export const PLAN_RUN_ROW_KINDS = ['workouts', 'meals'];
export const adminPlanRunSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    displayName: z.string().nullable(),
    /** Null once the week row itself is gone; the run outlives it deliberately. */
    planWeekId: z.string().uuid().nullable(),
    /** `workouts` for a run whose week has been deleted: only a weekly plan's can be. */
    kind: z.enum(PLAN_RUN_ROW_KINDS),
    /** The local date this run’s week starts on: the day it was asked for, since weeks roll from then. */
    weekStartDate: z.string(),
    trigger: z.enum(PLAN_RUN_TRIGGERS),
    status: z.enum(PLAN_RUN_STATUSES),
    /** 1 for the first draft, 2 once the repair round has run. */
    attempt: z.number().int(),
    /** Null while `running`, and for a run whose process died mid-flight. */
    durationMs: z.number().int().nullable(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    createdAt: z.string(),
});
export const adminPlanRunDetailSchema = adminPlanRunSchema.extend({
    /**
     * The validator's own message list, verbatim and unsummarised — "Thursday
     * protein 88g, floor 110g" is the whole of what an operator needs, and
     * rewording it here would lose the numbers.
     */
    validationFailure: z.string().nullable(),
    failureReason: z.string().nullable(),
    /**
     * What the feasibility precheck widened before the first model call, and
     * why. Null on almost every run, which is the point: a value here is the
     * product admitting it promised a number this user's own food cannot reach,
     * and that is a target to argue about rather than a bug to fix.
     */
    boundsRelaxation: z.string().nullable(),
    /** Where the week itself ended up, or null if it has since been deleted. */
    planWeekStatus: z.enum(PLAN_STATUSES).nullable(),
    /** Whether re-running generation for this week is offered, and why not. */
    retryable: z.boolean(),
    retryBlockedReason: z.string().nullable(),
});
export const adminPlanRunListQuerySchema = cursorPageQuerySchema.extend({
    /** Which plans to count. Everything, unless the screen narrows it. */
    kind: z.enum(PLAN_RUN_KINDS).default('all'),
    status: z.enum(PLAN_RUN_STATUSES).optional(),
    trigger: z.enum(PLAN_RUN_TRIGGERS).optional(),
    userId: z.string().uuid().optional(),
    /** Exact local date, so "everything planned for this start date" is one link. */
    weekStartDate: z.string().trim().max(10).optional(),
    /**
     * The subscriber's name or phone, or a whole run id.
     *
     * Distinct from `userId` and `weekStartDate`, which are exact: an operator
     * arriving from a support thread has a name, not a uuid. A string shaped
     * like a uuid is also matched against the run id, so an id pasted out of a
     * log line finds its run — but only a complete one, because the column is a
     * uuid and Postgres has no prefix operator for one.
     */
    search: z.string().trim().max(120).optional(),
});
export const adminPlanRunParamsSchema = z.object({ id: z.string().uuid() });
/**
 * What a retry did.
 *
 * A retry is a re-enqueue, not a synchronous regeneration: the week goes back to
 * `generating` and a worker picks it up. The result names the run that was
 * created so the console can follow it rather than guess which row is new.
 */
export const adminPlanRetryResultSchema = z.object({
    /** The run that was retried. */
    runId: z.string().uuid(),
    userId: z.string().uuid(),
    weekStartDate: z.string(),
    /** The week now queued for regeneration. */
    planWeekId: z.string().uuid(),
});
/**
 * The weekly re-plan cron, read from BullMQ rather than from a second copy of
 * the schedule. `nextRunAt` is what the queue itself intends to do next; a
 * constant in this repo would only say what it was configured to do.
 */
export const adminPlanCronSchema = z.object({
    queue: z.literal('weekly-replan'),
    /** Null when no repeatable job is registered — the cron is not scheduled. */
    pattern: z.string().nullable(),
    nextRunAt: z.string().nullable(),
    isPaused: z.boolean(),
    counts: queueCountsSchema,
});
/**
 * How long generation actually takes, so the app's give-up timeout is a
 * decision rather than a guess.
 *
 * The Flutter client polls with 1s→8s backoff and gives up at two minutes,
 * falling through to a "Still building your plan · Go to home" screen. Nobody
 * could say what share of users that reaches. These are that answer.
 */
/**
 * The client's own wait budget, in milliseconds — the point at which it stops
 * polling and shows the give-up screen.
 *
 * Here rather than only in the app because it is the threshold
 * `overBudgetShare` is measured against, and a number the console reports
 * against a threshold nobody can see is a number nobody can act on. It has to
 * be changed in both places together, and this comment is where the next
 * person finds that out.
 */
export const CLIENT_WAIT_BUDGET_MS = 120_000;
export const adminPlanDurationBucketSchema = z.object({
    /**
     * Runs that finished and recorded a duration. The percentiles are over these
     * and no others, which is why `unfinished` sits beside them: a window with
     * many abandoned runs has percentiles that look better than the experience.
     */
    runs: z.number().int().nonnegative(),
    /**
     * Started, never finished — the process died, or it is still going. Every
     * one of these is a user who waited past the client's budget, so they are
     * counted rather than quietly excluded.
     */
    unfinished: z.number().int().nonnegative(),
    /** Null when nothing in the window finished. */
    p50Ms: z.number().int().nonnegative().nullable(),
    p95Ms: z.number().int().nonnegative().nullable(),
    p99Ms: z.number().int().nonnegative().nullable(),
    /** Share of finished runs whose repair round ran, 0–1. */
    repairShare: z.number().min(0).max(1).nullable(),
    /**
     * Share of finished runs slower than `CLIENT_WAIT_BUDGET_MS`, 0–1 — users
     * who were shown the give-up screen even though their plan was on its way.
     */
    overBudgetShare: z.number().min(0).max(1).nullable(),
});
export const adminPlanDurationsSchema = z.object({
    windowDays: z.number().int().positive(),
    clientBudgetMs: z.number().int().positive(),
    overall: adminPlanDurationBucketSchema,
    /** One row per trigger that has a run in the window. */
    byTrigger: z.array(adminPlanDurationBucketSchema.extend({ trigger: z.enum(PLAN_RUN_TRIGGERS) })),
    /**
     * One row per status seen in the window - the scope pills' own population.
     *
     * Counted over runs, not over plan weeks. `/admin/overview` has numbers that
     * read like these and counts weeks instead, and a pill whose number
     * describes a different population from the table under it is worse than a
     * pill with no number at all.
     */
    byStatus: z.array(z.object({ status: z.enum(PLAN_RUN_STATUSES), runs: z.number().int() })),
});
export const adminPlanDurationsQuerySchema = z.object({
    /** How far back to look. A month by default — long enough to be stable. */
    windowDays: z.coerce.number().int().min(1).max(90).default(30),
    kind: z.enum(PLAN_RUN_KINDS).default('all'),
});
/**
 * What generation costs, by what asked for it.
 *
 * The question this answers is not "what do we spend on plans" — AI
 * operations already totals that — but "what does the part nobody asked for
 * cost": `weekly_cron` is spend the product decides to make, `user_request`
 * is spend a person asked for, and the two are different arguments. Joined
 * from `LlmCall` against the run id rather than stored on the run, because
 * the generation path does not know a call's price; only the LLM client does.
 */
export const adminPlanSpendBucketSchema = z.object({
    /** Runs in the window that made at least one model call. */
    runs: z.number().int().nonnegative(),
    /** People those runs were for. Distinct, so the overall row is not a sum. */
    users: z.number().int().nonnegative(),
    calls: z.number().int().nonnegative(),
    /**
     * Summed `cost_usd`, null when every call in the bucket is one the provider
     * reported no figure for. Zero and "not reported" are different facts and
     * the screen says which it has; an empty bucket, and one whose only
     * unpriced calls were refusals, really did cost nothing and read as $0.
     */
    costUsd: z.number().nullable(),
    /** Calls that succeeded or timed out and carry no price. `costUsd` is a floor while this is above zero. */
    costUnknown: z.number().int().nonnegative(),
});
export const adminPlanSpendSchema = z.object({
    windowDays: z.number().int().positive(),
    kind: z.enum(PLAN_RUN_KINDS),
    overall: adminPlanSpendBucketSchema,
    /** One row per trigger that spent anything in the window. */
    byTrigger: z.array(adminPlanSpendBucketSchema.extend({ trigger: z.enum(PLAN_RUN_TRIGGERS) })),
});
//# sourceMappingURL=plan-runs.js.map